import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Calendar, Mail, Phone, Globe } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-semibold mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-emerald-100">
          {/* Header */}
          <div className="border-b border-gray-100 pb-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
                <p className="text-emerald-600 font-medium">Journi (Pvt) Ltd</p>
              </div>
            </div>
            <p className="text-gray-600 font-medium mb-4">The rules and responsibilities governing use of the Journi platform</p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                Effective Date: June 2026 | Last Updated: June 2026
              </span>
            </div>
          </div>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/50">
            <a href="https://journi.pk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Globe className="w-4 h-4 text-emerald-600" />
              journi.pk
            </a>
            <a href="mailto:subscribe@journi.pk" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Mail className="w-4 h-4 text-emerald-600" />
              subscribe@journi.pk
            </a>
            <a href="https://wa.me/923312312345" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-700 hover:underline">
              <Phone className="w-4 h-4 text-emerald-600" />
              03312312345
            </a>
          </div>

          {/* Document Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="mb-3">
                These Terms of Service constitute a legally binding agreement between you and Journi (Pvt) Ltd ('Journi') governing your use of the Journi carpooling platform at journi.pk.
              </p>
              <p>
                By creating an account or using the Platform, you confirm you are at least 18 years of age, have legal capacity to contract, and agree to these Terms and our Privacy Policy.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Nature of the Platform</h2>
              <p className="mb-3">
                Journi is a technology platform connecting individuals who wish to share rides. Journi is NOT a transport company, taxi service, or ride-hailing service. Journi does not employ drivers, own vehicles, or control ride arrangements.
              </p>
              <p className="mb-3">
                Drivers and riders are independent individuals entering private arrangements.
              </p>
              <p>
                Journi provides coordination technology only and is not a party to any ride.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. Account Registration</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 Eligibility</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Minimum age: 18 years</li>
                    <li>Valid Pakistani mobile phone number required</li>
                    <li>Accurate and truthful information must be provided</li>
                    <li>One account per person – multiple accounts are prohibited</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 Account Security</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>You are responsible for all activity under your account</li>
                    <li>Report unauthorised access immediately to <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a></li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3.3 Verification</h3>
                  <p>
                    Optional CNIC, licence, and vehicle verification is available. Providing false documents constitutes fraud and may result in criminal liability under Pakistani law.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Driver Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Hold a valid Pakistani driver's licence for the vehicle driven</li>
                <li>Maintain roadworthy, registered, and insured vehicle</li>
                <li>Comply with all Pakistani traffic laws</li>
                <li>Never drive under the influence of alcohol or drugs</li>
                <li>Only offer rides on routes genuinely intended to travel</li>
                <li>Charge only the amount stated in the ride listing</li>
                <li>Be punctual and communicate promptly with confirmed riders</li>
                <li>Treat all riders with respect – no discrimination on prohibited grounds</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Rider Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Be at the agreed pickup location at the agreed time</li>
                <li>Notify driver promptly of cancellations or delays</li>
                <li>Pay the agreed fare directly to the driver</li>
                <li>Wear a seatbelt at all times</li>
                <li>Treat drivers with respect and professionalism</li>
                <li>Not bring prohibited or illegal items without driver agreement</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Prohibited Conduct</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Posting false, misleading, or fraudulent ride listings</li>
                <li>Impersonating another person or submitting false identity documents</li>
                <li>Harassment, threatening behaviour, or intimidation</li>
                <li>Sexual harassment or inappropriate conduct</li>
                <li>Using contact details for any purpose other than coordinating the specific shared ride</li>
                <li>Charging undisclosed fees to riders</li>
                <li>Creating multiple accounts or sharing accounts</li>
                <li>Manipulating ratings or reviews</li>
                <li>Posting rides not genuinely intended to collect contact information</li>
                <li>Any activity violating Pakistani law including PPC, PECA 2016, and PDPA 2023</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Contact Exchange Feature</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">7.1 Permitted Use</h3>
                  <p>
                    Contact details shared through the Platform may only be used to coordinate pickup time and location for the specific shared ride and to communicate changes or cancellations.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">7.2 Prohibited Use</h3>
                  <ul className="list-disc pl-6 space-y-1.5">
                    <li>Contacting the user for any unrelated purpose</li>
                    <li>Marketing or promoting any product or service</li>
                    <li>Sharing the phone number with any third party</li>
                    <li>Harassment after the ride has concluded</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">7.3 Reporting Misuse</h3>
                  <p>
                    Report inappropriate contact to <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a> or WhatsApp <a href="https://wa.me/923312312345" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">03312312345</a>. Misuse will result in account suspension.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Subscription and Payments</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">8.1 Free Tier</h3>
                  <p>
                    Every user receives 5 free contact exchanges on registration, plus referral bonuses.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">8.2 Journi Premium</h3>
                  <p>
                    Rs. 500/month for unlimited contact exchanges. Payment via JazzCash 03312312345 with WhatsApp screenshot confirmation to 03312312345 or <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a>.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">8.3 Refund Policy</h3>
                  <p>
                    Subscription fees are non-refundable once activated. Technical issues must be reported within 7 days for resolution.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Referral Programme</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Both referrer and new user receive 5 bonus exchanges on valid referral</li>
                <li>Self-referral and multi-account referral abuse is prohibited</li>
                <li>Referral bonuses have no cash value</li>
                <li>Journi may modify or discontinue the programme at any time</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10. Cancellations and No-Shows</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Cancel as early as possible – repeated last-minute cancellations may result in suspension</li>
                <li>No-shows may be reported through the Platform</li>
                <li>Journi does not offer compensation for no-shows – this is between driver and rider</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">11. Limitation of Liability</h2>
              <p className="mb-3">
                To the maximum extent permitted by Pakistani law, Journi shall not be liable for any injury, death, loss, or damage arising from rides arranged through the Platform, conduct of any user, or disputes between users.
              </p>
              <p>
                Maximum liability shall not exceed subscription fees paid in the 3 months preceding any claim.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">12. Indemnification</h2>
              <p>
                You agree to indemnify Journi, its officers and employees from any claims, damages, or expenses arising from your use of the Platform, breach of these Terms, violation of any law, or any ride you offer or take.
              </p>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">13. Intellectual Property</h2>
              <p>
                All Journi content, technology, and branding – including the Polypoints algorithm – is the exclusive property of Journi (Pvt) Ltd. Unauthorised use is prohibited.
              </p>
            </section>

            {/* Section 14 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">14. Account Suspension and Termination</h2>
              <p>
                Journi may suspend or permanently ban accounts for Terms violations, fraudulent activity, harassment, or conduct threatening user safety. You may delete your account by contacting <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a>.
              </p>
            </section>

            {/* Section 15 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">15. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the Islamic Republic of Pakistan. Disputes are subject to the exclusive jurisdiction of the courts of Islamabad. Before legal proceedings, contact <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a> for good-faith resolution.
              </p>
            </section>

            {/* Section 16 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">16. Changes to Terms</h2>
              <p>
                Material changes will be communicated via the Platform or to your registered phone number with at least 14 days' notice. Continued use constitutes acceptance.
              </p>
            </section>

            {/* Section 17 */}
            <section className="border-t border-gray-100 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">17. Contact Information</h2>
              <div className="space-y-2 text-gray-600 bg-gray-50 p-4 rounded-lg mb-4">
                <p><strong>Email:</strong> <a href="mailto:subscribe@journi.pk" className="text-emerald-600 hover:underline">subscribe@journi.pk</a></p>
                <p><strong>WhatsApp:</strong> <a href="https://wa.me/923312312345" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">03312312345</a></p>
                <p><strong>Platform:</strong> <a href="https://journi.pk" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">journi.pk</a></p>
              </div>
              <p className="text-sm font-semibold text-gray-900">
                Journi (Pvt) Ltd – Registered in Pakistan
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Powered by ByeBusyness.com
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
